import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent, SlideshowSelectEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
    this.slideshow?.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect, { signal });
    this.#updateZoomTabStops(this.slideshow?.current ?? 0);
    if (this.slideshow) {
      this.#visibilityObserver = new MutationObserver(() => this.#updateZoomTabStops(this.slideshow?.current ?? 0));
      this.#visibilityObserver.observe(this.slideshow, { subtree: true, attributes: true, attributeFilter: ['aria-hidden'] });
    }
  }

  #controller = new AbortController();
  #visibilityObserver;

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
    this.#visibilityObserver?.disconnect();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    if (this.closest('dialog, quick-add-dialog')) prepareRolyQuickAdd(source);

    if (this.dataset.rolyMediaKey && this.dataset.rolyMediaKey === newMediaGallery.dataset.rolyMediaKey) return;

    this.querySelectorAll('zentus-roly-video').forEach((video) => video.pause?.());
    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  #handleSlideshowSelect = (event) => {
    if (event.target !== this.slideshow) return;
    this.#updateZoomTabStops(event.detail.index);
  };

  #updateZoomTabStops(index) {
    this.slideshow?.slides?.forEach((slide, slideIndex) => {
      const button = slide.querySelector('.product-media-container__zoom-button');
      if (button instanceof HTMLElement) button.tabIndex = slide.getAttribute('aria-hidden') === 'false' ? 0 : -1;
    });
  }

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}

export function prepareRolyQuickAdd(root) {
  for (const gallery of root.querySelectorAll('media-gallery[data-roly-imported-ids]')) {
    gallery.dataset.rolyProductPage = 'false';
    delete gallery.dataset.rolyMediaKey;
    // Quick-add reuses fetched main-product markup; keep its native viewer/controls.
    for (const fitted of gallery.querySelectorAll('[data-fit]')) fitted.removeAttribute('data-fit');
    for (const control of gallery.querySelectorAll('.z-gallery-viewer-arrow, .z-thumbnail-rail-button')) control.remove();
    for (const rail of gallery.querySelectorAll('zentus-thumbnail-rail')) rail.replaceWith(...rail.childNodes);
    for (const image of gallery.querySelectorAll('.dialog-zoomed-gallery > .product-media-container--image')) image.setAttribute('on:click', '/close');
    if (gallery.dataset.rolyNativeSizes) {
      for (const image of gallery.querySelectorAll(':scope > slideshow-component .product-media__image')) {
        image.sizes = gallery.dataset.rolyNativeSizes;
      }
    }
    const imported = new Set(gallery.dataset.rolyImportedIds.split(',').filter(Boolean));
    if (!imported.size) continue;
    const slideshow = gallery.querySelector(':scope > slideshow-component');
    const slides = [...(slideshow?.querySelectorAll('slideshow-slide') || [])];
    const removed = new Set();
    slides.forEach((slide, index) => {
      if (imported.has(slide.querySelector('[data-media-id]')?.dataset.mediaId)) {
        slide.querySelectorAll('zentus-roly-video').forEach((video) => video.pause?.());
        removed.add(index);
        slide.remove();
      }
    });
    for (const list of gallery.querySelectorAll('.media-gallery__grid, .dialog-zoomed-gallery')) {
      for (const item of [...list.children]) {
        if (imported.has(item.querySelector('[data-media-id]')?.dataset.mediaId)) item.remove();
      }
    }
    const remaining = slides.filter((_, index) => !removed.has(index));
    const label = gallery.dataset.rolySlideLabel;
    const reindex = (element, index) => {
      for (const name of ['on:click', 'on:pointerenter']) {
        const action = element.getAttribute(name);
        if (action) element.setAttribute(name, action.replace(/\/\d+$/, '/' + index));
      }
    };
    for (const selector of ['[ref="thumbnails[]"]', '[ref="dots[]"]', '.dialog-thumbnails-list__thumbnail']) {
      const buttons = [...gallery.querySelectorAll(selector)];
      let index = 0;
      for (const [oldIndex, button] of buttons.entries()) {
        if (removed.has(oldIndex)) { button.remove(); continue; }
        reindex(button, index);
        if (label) button.setAttribute('aria-label', label.replace('__index__', String(index + 1)).replace('__length__', String(remaining.length)));
        if (index === 0) button.setAttribute('aria-current', 'true');
        else button.removeAttribute('aria-current');
        index++;
      }
    }
    for (const counter of gallery.querySelectorAll('.slideshow-controls__counter')) {
      const current = counter.querySelector('[ref="current"]');
      const slash = counter.querySelector('.slash');
      if (current && slash) { current.textContent = '1'; counter.replaceChildren(current, slash, String(remaining.length)); }
    }
    remaining.forEach((slide, index) => {
      slide.setAttribute('aria-hidden', String(index !== 0));
      slide.style.setProperty('--slideshow-timeline', '--slide-' + index);
      const zoom = slide.querySelector('.product-media-container__zoom-button');
      if (zoom) { reindex(zoom, index); zoom.tabIndex = index === 0 ? 0 : -1; }
    });
    gallery.querySelectorAll('.media-gallery__grid > li').forEach((item, index) => {
      const zoom = item.querySelector('.product-media-container__zoom-button');
      if (zoom) reindex(zoom, index);
    });
    slideshow?.setAttribute('initial-slide', '0');
    slideshow?.style.setProperty('--slideshow-timeline', remaining.map((_, index) => '--slide-' + index).join(', '));
    if (remaining.length <= 1) {
      gallery.querySelectorAll('slideshow-controls, slideshow-arrows').forEach((control) => control.remove());
      slideshow?.classList.add('product-media-gallery__slideshow--single-media', 'slideshow--single-media');
    }
    gallery.dataset.rolyImportedIds = '';
  }
  root.querySelectorAll('zentus-roly-technical-table').forEach((table) => table.remove());
}
